import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Empty, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { listarHistorico, type HistoricoItem } from '../api/homologacao';

const ACAO_LABELS: Record<string, string> = {
  criado: 'Criado',
  atualizado: 'Atualizado',
  finalizado: 'Finalizado',
  enviado: 'Enviado',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  arquivo_adicionado: 'Arquivo adicionado',
  arquivo_removido: 'Arquivo removido',
};

const ACAO_COLORS: Record<string, string> = {
  criado: 'green',
  finalizado: 'cyan',
  enviado: 'blue',
  aprovado: 'green',
  reprovado: 'red',
  arquivo_adicionado: 'geekblue',
  arquivo_removido: 'orange',
  atualizado: 'default',
};

const CATEGORIA_LABELS: Record<string, string> = {
  fotos_padrao: 'Fotos do Padrão',
  imagens_diversas_padrao: 'Ramal/Disjuntor/Medidor/Caixa',
  foto_relacao_caixas: 'Relação de Caixas',
  foto_fachada: 'Fachada da Casa',
  print_marcacao_telhado: 'Print Marcação do Telhado',
  print_localizacao: 'Print Localização Padrão',
  print_transformador: 'Print Transformador',
  foto_material_usina_existente: 'Material Usina Existente',
  doc_car: 'CAR (Cadastro Ambiental Rural)',
  doc_imovel: 'Documento do Imóvel',
  cessao_telhado: 'Cessão de Telhado',
  iptu_certidao: 'IPTU ou Certidão de Número',
  foto_telhado: 'Foto do Telhado',
  foto_estrutura_abaixo_telhado: 'Estrutura Abaixo do Telhado',
  foto_local_inversor: 'Local do Inversor',
  foto_acesso_telhado: 'Acesso ao Telhado',
  foto_quadro_conexao: 'Quadro de Conexão',
  foto_quadro_passagem: 'Quadro de Passagem',
  cartao_cnpj: 'Cartão CNPJ',
  contrato_social: 'Contrato Social',
  documentos_adicionais: 'Documentos Adicionais',
  doc_titular: 'Documento do Titular',
};

export default function HomologacaoHistoricoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<HistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    listarHistorico(Number(id))
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center p-20"><Spin size="large" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full border border-neutral-200 text-neutral-400 hover:text-black hover:border-black transition-colors">
          <ArrowLeftOutlined style={{ fontSize: 12 }} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-black tracking-tight">Histórico</h1>
          <span className="text-xs text-neutral-400 font-mono">Homologação #{id}</span>
        </div>
      </div>

      {items.length === 0 ? (
        <Empty description="Nenhum registro de histórico" />
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-neutral-200" />

          <div className="space-y-0">
            {items.map((h, i) => {
              const det = typeof h.detalhes === 'string' ? JSON.parse(h.detalhes) : h.detalhes;
              const date = new Date(h.created_at);
              const isFirst = i === 0;

              return (
                <div key={h.id} className="relative flex gap-4 pb-6">
                  {/* Dot */}
                  <div className={`relative z-10 w-[31px] shrink-0 flex justify-center`}>
                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${isFirst ? 'bg-black' : 'bg-neutral-300'}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag color={ACAO_COLORS[h.acao] || 'default'} className="text-xs">{ACAO_LABELS[h.acao] || h.acao}</Tag>
                      <span className="text-[11px] text-neutral-400">
                        {date.toLocaleDateString('pt-BR')} às {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {/* Details */}
                    {det && (
                      <div className="mt-1.5 text-sm text-neutral-600">
                        {det.nome_original && (
                          <p className="truncate">{det.nome_original}</p>
                        )}
                        {det.categoria && (
                          <span className="text-xs text-neutral-400">{CATEGORIA_LABELS[det.categoria as string] || det.categoria}</span>
                        )}
                        {det.status && !det.nome_original && (
                          <p className="text-xs text-neutral-400">Status: {det.status}</p>
                        )}
                      </div>
                    )}

                    {h.usuario && (
                      <p className="text-[11px] text-neutral-300 mt-1">por {h.usuario}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
